import { PenLineIcon, PlusIcon, UserIcon } from "lucide-react";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { NsKey } from "enums/NsKey";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { useMainBorrowerPricingActions, useMainBorrowerPricingState } from "../../contexts/main-borrower-pricing/MainBorrowerPricingContext";
import { BorrowerPricingLoanCards } from "./BorrowerPricingLoanCards";
import { BorrowerPricingInsuredCard } from "./BorrowerPricingInsuredCard";
import { useCommonPanelForm } from "../../hooks/useCommonPanelForm";
import { BorrowerPricingHeaderSkeleton } from "../loading/BorrowerPricingHeaderSkeleton";
import { usePanel } from "hooks/usePanel";
import { Sizes } from "components/ui/Loader";
import { BorrowerInsuredForm } from "features/subscription-process/profiling-process/forms/borrower-insured";
import { withPersonName } from "shared/utils/panelTitle";

export const BorrowerPricingHeader: React.FC = () => {
  const { insured, tabs, loansConfigurations, borrowerProjectId, borrowerId } = useMainBorrowerPricingState();
  const { initBorrowerPricingProcessAsync } = useMainBorrowerPricingActions();
  const { t } = useDynamicTranslation([NsKey.Common, NsKey.Borrower])
  const { onCreateLoan, onCreateInsured } = useCommonPanelForm()
  const navigate = useNavigate()
  const { openPanel } = usePanel()

  const reload = useCallback(() => {
    if (borrowerProjectId && borrowerId)
      initBorrowerPricingProcessAsync({ borrowerProjectId, emprunteurId: borrowerId })
  }, [borrowerProjectId, initBorrowerPricingProcessAsync, borrowerId])

  const onEditInsured = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!insured) return;
    openPanel({
      title: withPersonName(t("COMMON.LABELS.EDIT_INSURED_PERSON"), `${insured.firstName} ${insured.lastName}`),
      size: Sizes["2xl"],
    },
      <BorrowerInsuredForm reload={reload} personId={insured.id} borrowerProjectId={borrowerProjectId ?? undefined} />
    )
  }, [openPanel, t, insured, reload, borrowerProjectId])

  const switchInsured = useCallback((newBorrowerId: string) => {
    if (borrowerProjectId) {
      // Mettre a jour l'URL avec le nouveau borrowerId
      navigate(`/simulation/tarification/emprunteur/${borrowerProjectId}/${newBorrowerId}`, { replace: true })
      // Charger les donnees du nouvel emprunteur
      initBorrowerPricingProcessAsync({ borrowerProjectId, emprunteurId: newBorrowerId })
    }
  }, [borrowerProjectId, initBorrowerPricingProcessAsync, navigate])

  if (!insured) {
    return <BorrowerPricingHeaderSkeleton />
  }

  return (
    <div className="bg-white border border-gray-200 overflow-hidden rounded-xl shadow-sm">
      {/* Tabs container */}
      <div className="relative bg-gray-50 px-4 pt-3">
        {/* Tabs */}
        <div className="flex items-end gap-1">
          {tabs.map((tab, index) => {
            const isActive = tab.borrowerId === borrowerId;

            return (
              <div key={index} className="relative flex items-end">
                {/* Left curved corner */}
                {isActive && (
                  <>
                    {/* Masque pour cacher le coin arrondi du bouton */}
                    <div className="absolute left-0 bottom-0 w-2 h-2 bg-white z-20" />
                    <div
                      className="absolute -left-2 bottom-0 w-2 h-2 z-20"
                      style={{
                        background: 'radial-gradient(circle at 0 0, transparent 8px, white 8px)',
                      }}
                    >
                      <div
                        className="absolute w-2 h-2"
                        style={{
                          background: 'radial-gradient(circle at 0 0, transparent 7px, #e5e7eb 7px, #e5e7eb 8px, transparent 8px)',
                        }}
                      />
                    </div>
                  </>
                )}

                <button
                  onClick={() => switchInsured(tab.borrowerId)}
                  className={clsx(
                    "group relative flex items-center gap-2.5 px-4 py-2 transition-all duration-200 focus:outline-none",
                    isActive
                      ? "rounded-t-lg bg-white border border-gray-200 border-b-white -mb-px z-10 pb-[13px]"
                      : "rounded-lg border border-transparent hover:bg-gray-100 mb-[5px]"
                  )}
                >
                  <div className={clsx(
                    "flex items-center justify-center w-7 h-7 rounded-full transition-colors",
                    isActive
                      ? "bg-primary text-white"
                      : "bg-gray-200 text-gray-500 group-hover:bg-gray-300"
                  )}>
                    <UserIcon className="w-4 h-4" />
                  </div>

                  <span className={clsx(
                    "text-sm font-medium transition-colors whitespace-nowrap",
                    isActive ? "text-primary" : "text-gray-500 group-hover:text-gray-700"
                  )}>
                    {tab.fullName}
                  </span>

                  {isActive && (
                    <button
                      type="button"
                      onClick={onEditInsured}
                      className="flex items-center justify-center w-6 h-6 rounded-full hover:bg-primary/10 transition-colors"
                      title={t("COMMON.LABELS.EDIT_INSURED_PERSON")}
                    >
                      <PenLineIcon className="w-3.5 h-3.5 text-primary" />
                    </button>
                  )}
                </button>

                {/* Right curved corner */}
                {isActive && (
                  <>
                    {/* Masque pour cacher le coin arrondi du bouton */}
                    <div className="absolute right-0 bottom-0 w-2 h-2 bg-white z-20" />
                    <div
                      className="absolute -right-2 bottom-0 w-2 h-2 z-20"
                      style={{
                        background: 'radial-gradient(circle at 100% 0, transparent 8px, white 8px)',
                      }}
                    >
                      <div
                        className="absolute w-2 h-2"
                        style={{
                          background: 'radial-gradient(circle at 100% 0, transparent 7px, #e5e7eb 7px, #e5e7eb 8px, transparent 8px)',
                        }}
                      />
                    </div>
                  </>
                )}
              </div>
            );
          })}

          {/* Add insured button */}
          <button
            onClick={onCreateInsured}
            className="group flex items-center gap-2.5 px-4 py-2 mb-[5px] transition-colors hover:opacity-70 focus:outline-none"
          >
            <div className="flex items-center justify-center w-7 h-7 rounded-full border-2 border-dashed border-primary text-primary transition-colors">
              <PlusIcon className="w-4 h-4" />
            </div>
            <span className="text-sm font-medium text-primary transition-colors whitespace-nowrap">
              Ajouter un assuré
            </span>
          </button>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Add loan button */}
          {loansConfigurations.length > 1 && (
            <button
              onClick={onCreateLoan}
              className="group flex items-center gap-2.5 px-4 py-2 mb-[5px] transition-colors hover:opacity-70 focus:outline-none"
            >
              <div className="flex items-center justify-center w-7 h-7 rounded-full border-2 border-dashed border-primary text-primary transition-colors">
                <PlusIcon className="w-4 h-4" />
              </div>
              <span className="text-sm font-medium text-primary transition-colors whitespace-nowrap">
                {t("BORROWER.LABELS.ADD_LOAN")}
              </span>
            </button>
          )}
        </div>

        {/* Horizontal separator line */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gray-200" />
      </div>

      {/* Content */}
      <div className="relative p-4">
        <div className="flex gap-4">
          <div>
            <BorrowerPricingInsuredCard insured={insured} />
          </div>
          <div className="relative grow">
            <BorrowerPricingLoanCards
              insured={insured}
              loansConfigurations={loansConfigurations}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
